import { FileText, Tags, Users } from 'lucide-react'
import React from 'react'
import Highlighter from 'react-highlight-words'
import { components, GroupBase, OptionProps } from 'react-select'

import { MasterBillOption } from '../types/masterBill'
import { deepSearchMatch } from '../utils/masterBillSearch'

import Autocomplete from '@/components/FormInteraction/Autocomplete'

// 自定义 Option 组件 - 显示复杂的层级结构并高亮
const CustomMasterBillOption: React.ComponentType<
  OptionProps<MasterBillOption, boolean, GroupBase<MasterBillOption>>
> = (props) => {
  const { data, selectProps } = props
  const searchText = selectProps.inputValue || ''

  // 判断分提单是否匹配
  const isHouseBillMatch = (hb: {
    id: number
    houseBlNumber: string
    cargoDetails?: Array<{
      id: number
      cargoNumber: string
      packages: number
    }>
  }) => {
    const search = searchText.toLowerCase()
    if (!search) return true
    if (hb.houseBlNumber?.toLowerCase().includes(search)) return true
    return hb.cargoDetails?.some(
      (c: { cargoNumber: string }) =>
        c.cargoNumber?.toLowerCase().includes(search)
    )
  }

  // 判断货号是否匹配
  const isCargoMatch = (cargo: { cargoNumber: string }) => {
    const search = searchText.toLowerCase()
    if (!search) return true
    return cargo.cargoNumber?.toLowerCase().includes(search)
  }

  // 过滤匹配的分提单
  const matchedHouseBills =
    data.houseBills?.filter((hb) => isHouseBillMatch(hb)) || []

  // 收集所有匹配的 HBL 编号
  const allMatchedHBLs = matchedHouseBills
    .map((hb) => hb.houseBlNumber)
    .join(', ')

  // 收集所有匹配的 Cargo 编号
  const allMatchedCargos = matchedHouseBills
    .flatMap((hb) => hb.cargoDetails?.filter((c) => isCargoMatch(c)) || [])
    .map((cargo) => cargo.cargoNumber)
    .join(', ')

  return (
    <components.Option {...props}>
      <div className="py-1.5">
        {/* 主提单信息 */}
        <div className="flex items-start gap-2">
          <FileText className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            {/* 第一行：主提单号、柜号、状态 */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">
                <Highlighter
                  highlightClassName="bg-yellow-200 font-bold"
                  searchWords={[searchText]}
                  autoEscape={true}
                  textToHighlight={data.masterBlNumber}
                />
              </span>
              {data.containerNumber && (
                <span className="text-xs text-gray-500">
                  <Highlighter
                    highlightClassName="bg-yellow-200 font-bold"
                    searchWords={[searchText]}
                    autoEscape={true}
                    textToHighlight={`柜号: ${data.containerNumber}`}
                  />
                </span>
              )}
              {data.statusDisplay && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                  {data.statusDisplay}
                </span>
              )}
            </div>

            {/* 第二行：HBL */}
            {allMatchedHBLs && (
              <div className="mt-1 flex items-start gap-1">
                <Users className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-gray-600 break-all">
                  <Highlighter
                    highlightClassName="bg-yellow-200 font-bold"
                    searchWords={[searchText]}
                    autoEscape={true}
                    textToHighlight={allMatchedHBLs}
                  />
                </span>
              </div>
            )}

            {/* 第三行：货号 */}
            {allMatchedCargos && (
              <div className="mt-0.5 ml-4 flex items-start gap-1">
                <Tags className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5"  />
                <span className="text-xs text-gray-500 break-all">
<Highlighter
                  highlightClassName="bg-yellow-200 font-bold"
                  searchWords={[searchText]}
                  autoEscape={true}
                  textToHighlight={allMatchedCargos}
                />
                </span>
                
              </div>
            )}
          </div>
        </div>
      </div>
    </components.Option>
  )
}

// MasterBillAutocomplete 组件
interface MasterBillAutocompleteProps {
  options: MasterBillOption[]
  value?: MasterBillOption | null
  onChange: (value: MasterBillOption | null) => void
  placeholder?: string
  isLoading?: boolean
  error?: string
  label?: string
  required?: boolean
  className?: string
}

const MasterBillAutocomplete: React.FC<MasterBillAutocompleteProps> = ({
  options,
  value,
  onChange,
  placeholder = '搜索主提单号、分提单号或货号...',
  isLoading = false,
  error,
  required,
  className
}) => {
  // 包装 onChange 以确保类型正确
  const handleChange = (
    newValue: MasterBillOption | MasterBillOption[] | null
  ) => {
    // 因为不是 multi，所以一定是单个值或 null
    if (Array.isArray(newValue)) {
      onChange(newValue[0] || null)
    } else {
      onChange(newValue)
    }
  }

  return (
    <Autocomplete<MasterBillOption>
      options={options}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      isLoading={isLoading}
      error={error}
      required={required}
      className={className}
      customComponents={{
        Option: CustomMasterBillOption
      }}
      customFilterOption={deepSearchMatch}
    />
  )
}

export default MasterBillAutocomplete
